import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, ArrowLeft, RotateCcw } from "lucide-react";

interface MatchCard {
  id: number;
  emoji: string;
  matchId: number;
  flipped: boolean;
  matched: boolean;
}

const CARD_SETS = [
  ["🐱", "🐶", "🐰", "🦊", "🐻", "🐼"],
  ["🍎", "🍌", "🍇", "🍊", "🍓", "🍉"],
  ["⭐", "🌙", "☀️", "🌈", "⛅", "❄️"],
  ["🚗", "🚀", "✈️", "🚢", "🚂", "🚲"],
];

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface Props {
  onComplete: (score: number, stars: number, activityKey: string) => void;
  onBack: () => void;
}

export default function MatchingGameActivity({ onComplete, onBack }: Props) {
  const [setIdx, setSetIdx] = useState<number | null>(null);
  const [cards, setCards] = useState<MatchCard[]>([]);
  const [flippedIds, setFlippedIds] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [totalPairs, setTotalPairs] = useState(0);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState({ score: 0, stars: 0 });
  const [isChecking, setIsChecking] = useState(false);

  const startGame = (idx: number) => {
    setSetIdx(idx);
    const emojis = CARD_SETS[idx];
    const pairs = emojis.slice(0, 6);
    setTotalPairs(pairs.length);
    const allCards: MatchCard[] = shuffleArray(
      pairs.flatMap((emoji, i) => [
        { id: i * 2, emoji, matchId: i, flipped: false, matched: false },
        { id: i * 2 + 1, emoji, matchId: i, flipped: false, matched: false },
      ])
    );
    setCards(allCards);
    setFlippedIds([]);
    setMoves(0);
    setMatchedPairs(0);
    setFinished(false);
  };

  const handleCardClick = useCallback((id: number) => {
    if (isChecking) return;
    const card = cards.find(c => c.id === id);
    if (!card || card.flipped || card.matched) return;
    if (flippedIds.length >= 2) return;

    const newFlipped = [...flippedIds, id];
    setFlippedIds(newFlipped);
    setCards(prev => prev.map(c => c.id === id ? { ...c, flipped: true } : c));

    if (newFlipped.length === 2) {
      setMoves(prev => prev + 1);
      setIsChecking(true);
      const [firstId, secondId] = newFlipped;
      const first = cards.find(c => c.id === firstId)!;
      const second = cards.find(c => c.id === id)!;

      if (first.matchId === second.matchId) {
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            c.matchId === first.matchId ? { ...c, matched: true, flipped: true } : c
          ));
          setMatchedPairs(prev => {
            const newMatched = prev + 1;
            if (newMatched >= totalPairs) {
              const finalMoves = moves + 1;
              const optimalMoves = totalPairs;
              const ratio = optimalMoves / finalMoves;
              const score = Math.max(1, Math.min(10, Math.round(ratio * 10)));
              const stars = score >= 8 ? 3 : score >= 5 ? 2 : 1;
              setResult({ score, stars });
              setFinished(true);
              onComplete(score, stars, "creative_matching");
            }
            return newMatched;
          });
          setFlippedIds([]);
          setIsChecking(false);
        }, 500);
      } else {
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            newFlipped.includes(c.id) && !c.matched ? { ...c, flipped: false } : c
          ));
          setFlippedIds([]);
          setIsChecking(false);
        }, 800);
      }
    }
  }, [cards, flippedIds, isChecking, moves, totalPairs, onComplete]);

  if (finished) {
    return (
      <Card className="text-center border-2 border-indigo-300 bg-gradient-to-b from-indigo-50 to-white">
        <CardContent className="p-8 space-y-4">
          <div className="text-6xl">🎯</div>
          <h2 className="text-2xl font-bold text-foreground">أحسنت! وجدت كل الأزواج!</h2>
          <div className="flex justify-center gap-1">
            {[1, 2, 3].map(s => (
              <Star key={s} className={`h-10 w-10 ${s <= result.stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
            ))}
          </div>
          <p className="text-lg text-muted-foreground">أنهيت في {moves} محاولة - {result.score} نقاط</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => startGame(setIdx!)} variant="outline">
              <RotateCcw className="h-4 w-4 ml-1" /> إعادة
            </Button>
            <Button onClick={onBack} variant="outline">
              <ArrowLeft className="h-4 w-4 ml-2" /> العودة
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (setIdx === null) {
    return (
      <div className="space-y-4">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Card className="border-2 border-indigo-200">
          <CardHeader className="text-center">
            <div className="text-5xl mb-2">🃏</div>
            <CardTitle className="text-xl">اختر مجموعة للمطابقة!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "حيوانات", emoji: "🐾" },
                { label: "فواكه", emoji: "🍎" },
                { label: "طقس", emoji: "🌈" },
                { label: "مركبات", emoji: "🚗" },
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={() => startGame(i)}
                  className="p-4 rounded-xl border-2 border-muted hover:border-indigo-400 bg-card hover:bg-indigo-50 transition-all text-center"
                >
                  <div className="text-4xl mb-2">{item.emoji}</div>
                  <p className="font-medium text-foreground">{item.label}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button onClick={() => setSetIdx(null)} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <div className="flex gap-2">
          <Badge variant="secondary">🔄 {moves} محاولة</Badge>
          <Badge variant="secondary">✅ {matchedPairs}/{totalPairs}</Badge>
        </div>
      </div>

      <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-lg">اعثر على الأزواج المتطابقة! 🃏</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-2 max-w-sm mx-auto">
            {cards.map(card => (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                disabled={card.flipped || card.matched || isChecking}
                className={`aspect-square rounded-xl text-3xl flex items-center justify-center border-2 transition-all ${
                  card.matched
                    ? "bg-green-100 border-green-400 scale-95"
                    : card.flipped
                    ? "bg-white border-primary shadow-lg scale-105"
                    : "bg-gradient-to-br from-indigo-400 to-purple-500 border-indigo-300 hover:scale-105 hover:shadow-md cursor-pointer"
                }`}
              >
                {card.flipped || card.matched ? card.emoji : "❓"}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
